import { useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from 'framer-motion';

export interface DockItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  active?: boolean;
}

/**
 * Floating Dock（Aceternity UI 风格）— macOS 风格放大导航栏
 */
export function FloatingDock({ items }: { items: DockItem[] }) {
  const mouseX = useMotionValue(Infinity);

  return (
    <div className="flex items-end gap-3 px-3 py-2">
      {items.map((item) => (
        <DockIcon key={item.id} item={item} mouseX={mouseX} />
      ))}
    </div>
  );
}

function DockIcon({ item, mouseX }: { item: DockItem; mouseX: ReturnType<typeof useMotionValue<number>> }) {
  const ref = useRef<HTMLButtonElement>(null);
  const [hovered, setHovered] = useState(false);

  const distance = useTransform(mouseX, (val) => {
    const bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 };
    return val - bounds.x - bounds.width / 2;
  });

  const widthSync = useTransform(distance, [-140, 0, 140], [44, 68, 44]);
  const width = useSpring(widthSync, { stiffness: 300, damping: 22 });
  const Icon = item.icon;

  return (
    <motion.button
      ref={ref}
      style={{ width }}
      onClick={item.onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`relative aspect-square rounded-2xl flex items-center justify-center transition-colors ${
        item.active ? 'brand-gradient text-white glow-primary' : 'bg-card/80 text-text-muted hover:text-text border border-border-soft'
      }`}
    >
      <AnimatePresence>
        {hovered && (
          <motion.span
            initial={{ opacity: 0, y: 6, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 24 }}
            className="absolute -top-9 whitespace-nowrap rounded-lg bg-card/95 border border-border-soft px-2.5 py-1 text-[11px] font-semibold text-text shadow-lg"
          >
            {item.label}
          </motion.span>
        )}
      </AnimatePresence>
      <Icon className="w-5 h-5" />
    </motion.button>
  );
}
